import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import logo from "../../../images/ensss_logo.png";

/* =========================================================
 * Même système de polices/couleurs/mise en page que le relevé
 * de notes (TranscriptPDF.jsx) - voir ce fichier pour le détail
 * des choix. On ne duplique ici que ce qui est nécessaire au PV
 * d'incident : en-tête, carte d'informations, description,
 * validation/signature, pied de page.
 * ======================================================= */

Font.register({
    family: "Louguiya",
    src: "/fonts/Louguiya.ttf",
    fontWeight: "normal",
});

Font.register({
    family: "Louguiya",
    src: "/fonts/Louguiya Bold.ttf",
    fontWeight: "bold",
});

Font.register({
    family: "LouguiyaFR",
    src: "/fonts/LouguiyaFR.ttf",
    fontWeight: "normal",
});

Font.register({
    family: "LouguiyaFR",
    src: "/fonts/LouguiyaFR Bold.ttf",
    fontWeight: "bold",
});

/* =========================================================
 * HELPERS (identiques à TranscriptPDF.jsx)
 * ======================================================= */

const containsArabic = (value) => {
    if (value === null || value === undefined) {
        return false;
    }
    return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(String(value));
};

const isArabicLanguage = (language) => {
    return String(language || "").toLowerCase().startsWith("ar");
};

const getTextStyle = (value, baseStyle, bold = false) => {
    const arabic = containsArabic(value);
    return [
        baseStyle,
        {
            fontFamily: arabic ? "Louguiya" : "LouguiyaFR",
            fontWeight: bold ? "bold" : "normal",
        },
        arabic && styles.arabicText,
    ];
};

const formatLongDate = (date, language = "fr") => {
    if (!date) return "-";
    try {
        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return "-";
        return new Intl.DateTimeFormat(isArabicLanguage(language) ? "ar-TN" : "fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(parsedDate);
    } catch (error) {
        return "-";
    }
};

const formatDateTime = (date, language = "fr") => {
    if (!date) return "-";
    try {
        const parsedDate = new Date(date);
        if (Number.isNaN(parsedDate.getTime())) return "-";
        return new Intl.DateTimeFormat(isArabicLanguage(language) ? "ar-TN" : "fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(parsedDate);
    } catch (error) {
        return "-";
    }
};

/* =========================================================
 * EN-TÊTE (identique au relevé de notes)
 * ======================================================= */

const PvHeader = ({ t, language, isArabic }) => {
    const schoolName = t("pages.results.institutionName", {
        defaultValue: "École Nationale Supérieure de Sécurité Sociale",
    });
    const documentDate = formatLongDate(new Date(), language);
    const documentLabel = t("pages.results.document", { defaultValue: "DOCUMENT" });
    const documentTitle = t("pages.examIncidents.pvTitle", {
        defaultValue: "Procès-verbal d'incident d'examen",
    });

    return (
        <>
            <View style={styles.topBar} />
            <View style={styles.header}>
                <View style={[styles.headerRow, isArabic && styles.headerRowRtl]}>
                    <View style={[styles.institutionSection, isArabic && styles.institutionSectionRtl]}>
                        <Image src={logo} style={[styles.logo, isArabic && styles.logoRtl]} />
                        <View style={[styles.institutionText, isArabic && styles.institutionTextRtl]}>
                            <Text style={getTextStyle(schoolName, styles.institutionName, true)}>
                                {schoolName}
                            </Text>
                        </View>
                    </View>

                    <View style={[styles.documentInfo, isArabic && styles.documentInfoRtl]}>
                        <Text style={getTextStyle(documentLabel, styles.documentLabel, true)}>
                            {documentLabel}
                        </Text>
                        <Text style={getTextStyle(documentTitle, styles.documentTitle, true)}>
                            {documentTitle}
                        </Text>
                        <Text style={getTextStyle(documentDate, styles.documentDate)}>
                            {documentDate}
                        </Text>
                    </View>
                </View>
            </View>

            <View style={[styles.titleSection, isArabic && styles.titleSectionRtl]}>
                <Text style={getTextStyle(documentTitle, styles.title, true)}>
                    {documentTitle}
                </Text>
            </View>
        </>
    );
};

/* =========================================================
 * CARTE D'INFORMATIONS DE L'INCIDENT
 * (même gabarit que la carte "informations de l'étudiant" du relevé)
 * ======================================================= */

const IncidentCard = ({ incident, context, t, isArabic }) => {
    const typeLabels = {
        fraud: t("pages.examIncidents.fraude", { defaultValue: "Fraude" }),
        technical_interruption: t("pages.examIncidents.interruptionTechnique", {
            defaultValue: "Interruption technique",
        }),
        other: t("pages.examIncidents.autre", { defaultValue: "Autre" }),
    };

    const informationTitle = t("pages.examIncidents.informationsIncident", {
        defaultValue: "Informations de l'incident",
    });
    const typeLabel = t("pages.examIncidents.typeIncident", { defaultValue: "Type d'incident" });
    const sessionLabel = t("pages.examIncidents.sessionExamen", { defaultValue: "Session d'examen" });
    const roomLabel = t("pages.examIncidents.salle", { defaultValue: "Salle" });
    const studentLabel = t("pages.examIncidents.etudiantConcerne", { defaultValue: "Étudiant concerné" });
    const reporterLabel = t("pages.examIncidents.constatePar", { defaultValue: "Constaté par" });
    const dateLabel = t("common.ui.date", { defaultValue: "Date/heure" });

    const fields = [
        [typeLabel, typeLabels[incident.type] || incident.type || "-"],
        [sessionLabel, context.sessionLabel || "-"],
        [roomLabel, context.roomLabel || "-"],
        [studentLabel, context.studentLabel || "-"],
        [reporterLabel, context.reporterLabel || "-"],
        [dateLabel, formatDateTime(incident.created_at, context.language)],
    ];

    return (
        <View style={styles.studentCard}>
            <View style={[styles.studentCardHeader, isArabic && styles.studentCardHeaderRtl]}>
                <Text style={getTextStyle(informationTitle, styles.sectionTitle, true)}>
                    {informationTitle}
                </Text>
            </View>

            <View style={[styles.studentGrid, isArabic && styles.studentGridRtl]}>
                {fields.map(([label, value]) => (
                    <View style={styles.studentField} key={label}>
                        <Text style={getTextStyle(label, styles.fieldLabel)}>{label}</Text>
                        <Text style={getTextStyle(value, styles.fieldValue, true)}>{value}</Text>
                    </View>
                ))}
            </View>
        </View>
    );
};

/* =========================================================
 * DESCRIPTION DES FAITS
 * ======================================================= */

const DescriptionSection = ({ incident, t, isArabic }) => {
    const descriptionTitle = t("pages.examIncidents.descriptionFaitsConstates", {
        defaultValue: "Description des faits constatés",
    });
    const description = incident.description || "-";

    return (
        <View style={styles.studentCard}>
            <View style={[styles.studentCardHeader, isArabic && styles.studentCardHeaderRtl]}>
                <Text style={getTextStyle(descriptionTitle, styles.sectionTitle, true)}>
                    {descriptionTitle}
                </Text>
            </View>
            <View style={styles.descriptionBody}>
                <Text style={getTextStyle(description, styles.descriptionText)}>
                    {description}
                </Text>
            </View>
        </View>
    );
};

/* =========================================================
 * VALIDATION / SIGNATURE (même gabarit que le relevé de notes)
 * ======================================================= */

const ValidationSection = ({ t }) => {
    const validationTitle = t("pages.results.validation", { defaultValue: "Validation" });
    const summaryText = t("pages.examIncidents.pvValidationSummary", {
        defaultValue: "Le présent procès-verbal atteste des faits constatés lors de l'épreuve.",
    });
    const signatureLabel = t("pages.examIncidents.signatureSurveillant", {
        defaultValue: "Signature du surveillant / responsable",
    });

    return (
        <View style={styles.validationSection}>
            <View style={styles.validationHeader}>
                <Text style={getTextStyle(validationTitle, styles.validationTitle, true)}>
                    {validationTitle}
                </Text>
            </View>
            <View style={styles.validationContent}>
                <View style={styles.validationTextBlock}>
                    <Text style={getTextStyle(summaryText, styles.validationText)}>
                        {summaryText}
                    </Text>
                </View>
                <View style={styles.signatureBox}>
                    <Text style={getTextStyle(signatureLabel, styles.signatureLabel)}>
                        {signatureLabel}
                    </Text>
                    <View style={styles.signatureLine} />
                </View>
            </View>
        </View>
    );
};

/* =========================================================
 * PIED DE PAGE (identique au relevé de notes)
 * ======================================================= */

const Footer = ({ t, language }) => {
    const generatedText = t("pages.results.generatedDocument", {
        defaultValue: "Document généré automatiquement.",
    });
    const currentDate = formatLongDate(new Date(), language);

    return (
        <View fixed style={styles.footer}>
            <Text style={getTextStyle(generatedText, styles.footerText)}>{generatedText}</Text>
            <Text style={getTextStyle(currentDate, styles.footerText)}>{currentDate}</Text>
            <Text
                style={styles.pageNumber}
                render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            />
        </View>
    );
};

/* =========================================================
 * STYLES (repris à l'identique de TranscriptPDF.jsx pour les
 * blocs communs - en-tête, carte d'informations, validation,
 * pied de page - afin de garder le même modèle visuel)
 * ======================================================= */

const COLORS = {
    primary: "#1f4e79",
    primaryDark: "#163a5c",
    secondary: "#5b7083",
    text: "#1f2937",
    muted: "#6b7280",
    border: "#d7dde5",
    background: "#f5f7fa",
    white: "#ffffff",
};

const styles = StyleSheet.create({
    page: {
        paddingTop: 36,
        paddingBottom: 55,
        paddingHorizontal: 40,
        fontFamily: "LouguiyaFR",
        fontSize: 9,
        color: COLORS.text,
        backgroundColor: COLORS.white,
    },
    arabicPage: { fontFamily: "Louguiya", direction: "rtl" },
    arabicText: { fontFamily: "Louguiya", direction: "rtl", textAlign: "right" },

    topBar: { height: 5, backgroundColor: COLORS.primary, marginBottom: 12 },
    header: { width: "100%", borderBottomWidth: 1, borderBottomColor: COLORS.border, paddingBottom: 12 },
    headerRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
    headerRowRtl: { flexDirection: "row-reverse" },
    institutionSection: { flexDirection: "row", alignItems: "center", flex: 1 },
    institutionSectionRtl: { flexDirection: "row-reverse" },
    logo: { width: 58, height: 58, objectFit: "contain", marginRight: 14 },
    logoRtl: { marginRight: 0, marginLeft: 14 },
    institutionText: { flex: 1 },
    institutionTextRtl: { alignItems: "flex-end" },
    institutionName: { fontFamily: "LouguiyaFR", fontSize: 16, color: COLORS.primary, marginBottom: 4 },
    documentInfo: { width: 170, alignItems: "flex-end" },
    documentInfoRtl: { alignItems: "flex-start" },
    documentLabel: { fontFamily: "LouguiyaFR", fontSize: 7, color: COLORS.muted, marginBottom: 3 },
    documentTitle: { fontFamily: "LouguiyaFR", fontSize: 10, color: COLORS.primaryDark, marginBottom: 3 },
    documentDate: { fontFamily: "LouguiyaFR", fontSize: 8, color: COLORS.muted },

    titleSection: { marginTop: 18, marginBottom: 15, alignItems: "center" },
    titleSectionRtl: { direction: "rtl" },
    title: { fontFamily: "LouguiyaFR", fontSize: 18, color: COLORS.primaryDark, textAlign: "center" },

    studentCard: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        marginBottom: 15,
        overflow: "hidden",
    },
    studentCardHeader: {
        paddingVertical: 7,
        paddingHorizontal: 10,
        backgroundColor: COLORS.background,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
    },
    studentCardHeaderRtl: { alignItems: "flex-end" },
    sectionTitle: { fontFamily: "LouguiyaFR", fontSize: 10, color: COLORS.primaryDark },
    studentGrid: { flexDirection: "row", flexWrap: "wrap", padding: 8 },
    studentGridRtl: { flexDirection: "row-reverse" },
    studentField: { width: "50%", paddingHorizontal: 7, paddingVertical: 5 },
    fieldLabel: { fontFamily: "LouguiyaFR", fontSize: 6.5, color: COLORS.muted, marginBottom: 2 },
    fieldValue: { fontFamily: "LouguiyaFR", fontSize: 8, color: COLORS.text },

    descriptionBody: { padding: 10 },
    descriptionText: {
        fontFamily: "LouguiyaFR",
        fontSize: 9,
        color: COLORS.text,
        lineHeight: 1.5,
        textAlign: "justify",
    },

    validationSection: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        marginTop: 5,
        marginBottom: 20,
        overflow: "hidden",
    },
    validationHeader: {
        backgroundColor: COLORS.background,
        paddingVertical: 7,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
    },
    validationTitle: { fontFamily: "LouguiyaFR", fontSize: 10, color: COLORS.primaryDark },
    validationContent: { padding: 12, flexDirection: "row", justifyContent: "space-between" },
    validationTextBlock: { width: "55%", paddingRight: 15 },
    validationText: { fontFamily: "LouguiyaFR", fontSize: 8, color: COLORS.text, lineHeight: 1.5 },
    signatureBox: { width: "35%", alignItems: "center", justifyContent: "flex-end", minHeight: 55 },
    signatureLabel: { fontFamily: "LouguiyaFR", fontSize: 7, color: COLORS.muted, textAlign: "center", marginBottom: 20 },
    signatureLine: { width: "90%", borderBottomWidth: 1, borderBottomColor: COLORS.text },

    footer: {
        position: "absolute",
        bottom: 25,
        left: 40,
        right: 40,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    footerText: { fontFamily: "LouguiyaFR", fontSize: 6.5, color: COLORS.muted },
    pageNumber: { fontFamily: "LouguiyaFR", fontSize: 6.5, color: COLORS.muted },
});

/* =========================================================
 * DOCUMENT PRINCIPAL
 * ======================================================= */

const IncidentPvPDF = ({ incident = {}, context = {} }) => {
    const { t, i18n } = useTranslation();
    const language = i18n.language || "fr";
    const isArabic = isArabicLanguage(language);

    const documentTitle = t("pages.examIncidents.pvTitle", {
        defaultValue: "Procès-verbal d'incident d'examen",
    });

    return (
        <Document title={documentTitle} author="ENSSS" subject={documentTitle} creator="ENSSS">
            <Page size="A4" style={[styles.page, isArabic && styles.arabicPage]}>
                <PvHeader t={t} language={language} isArabic={isArabic} />
                <IncidentCard incident={incident} context={{ ...context, language }} t={t} isArabic={isArabic} />
                <DescriptionSection incident={incident} t={t} isArabic={isArabic} />
                <ValidationSection t={t} />
                <Footer t={t} language={language} />
            </Page>
        </Document>
    );
};

export default IncidentPvPDF;
